import { type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, LogIn, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LogoShield } from "@/components/LogoShield";
import { cn } from "@/lib/utils";

type CampaignLayoutProps = {
  children: ReactNode;
  helpHref: string;
  currentPath: "landing" | "help";
  stickyCta?: ReactNode;
  headerCtaLabel?: string;
  headerCtaHref?: string;
};

export const CAMPAIGN_SIGNUP_HREF =
  "/portal/sign-up?plan=essential&source=linkedin-portfolio-campaign";

export const CAMPAIGN_SUPPORT_HREF =
  "mailto:support@shadowscan.com.au?subject=ShadowScan%20domain%20onboarding%20help";

const headerLinks = {
  landing: {
    help: "/help/add-domains",
  },
  help: {
    help: "/help/add-domains",
  },
};

export const CampaignLayout = ({
  children,
  helpHref,
  currentPath,
  stickyCta,
  headerCtaLabel,
  headerCtaHref,
}: CampaignLayoutProps) => {
  const links = currentPath === "landing" ? headerLinks.landing : headerLinks.help;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="fixed inset-x-0 top-0 z-50 border-b border-border/80 bg-background/90 backdrop-blur-xl">
        <div className="container flex h-16 items-center justify-between gap-4 px-4">
          <Link to="/linkedin" className="flex items-center gap-3">
            <div className="relative">
              <LogoShield className="h-7 w-7" />
              <div className="absolute inset-0 rounded-full bg-primary/25 blur-md" />
            </div>
            <span className="font-heading text-lg font-bold tracking-tight">
              <span className="text-foreground">Shadow</span>
              <span className="text-primary">Scan</span>
            </span>
          </Link>

          <div className="hidden items-center gap-2 md:flex">
            <Link
              to={links.help}
              className="rounded-md px-3 py-2 text-sm text-muted-foreground transition hover:bg-secondary hover:text-foreground"
            >
              Onboarding help
            </Link>
            <Button asChild variant="ghost" className="text-sm">
              <Link to="/portal/login">
                <LogIn className="h-4 w-4" />
                Portal login
              </Link>
            </Button>
            {headerCtaLabel && headerCtaHref ? (
              <CampaignHeaderCta label={headerCtaLabel} href={headerCtaHref} />
            ) : null}
          </div>
        </div>
      </div>

      <main className={cn("relative overflow-hidden pt-16", stickyCta ? "pb-24 md:pb-0" : "")}>
        <div className="pointer-events-none absolute inset-0 bg-grid-pattern opacity-[0.16]" />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-[420px] bg-[radial-gradient(circle_at_top,rgba(25,212,230,0.16),transparent_58%)]" />
        <div className="pointer-events-none absolute right-0 top-40 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />
        <div className="pointer-events-none absolute left-0 top-[30rem] h-80 w-80 rounded-full bg-primary/10 blur-3xl" />
        <div className="relative">{children}</div>
      </main>

      <footer className="border-t border-border/80 bg-card/70">
        <div className="container px-4 py-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <LogoShield className="h-6 w-6" />
                <span className="font-heading text-sm uppercase tracking-[0.24em] text-primary">
                  ShadowScan
                </span>
              </div>
              <p className="max-w-xl text-sm leading-relaxed text-muted-foreground">
                WordPress security for agencies and multi-site operators, from free Guard and
                Basic Hosting to monitored plans with human oversight where included. ShadowScan is part of Valnex Group.
              </p>
            </div>

            <div className="flex flex-col items-start gap-3 text-sm text-muted-foreground md:items-end">
            <div className="flex flex-wrap items-center gap-3">
                <a
                  href={helpHref}
                  className="transition hover:text-foreground"
                >
                  Onboarding help
                </a>
                <a
                  href={CAMPAIGN_SUPPORT_HREF}
                  className="inline-flex items-center gap-2 transition hover:text-foreground"
                >
                  <Mail className="h-4 w-4" />
                  support@shadowscan.com.au
                </a>
              </div>
              <div className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background/60 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                <span aria-hidden="true" className="h-2 w-2 rounded-full bg-[#22C373]" />
                for agencies and multi-site WordPress operators
              </div>
            </div>
          </div>
        </div>
      </footer>

      {stickyCta ? (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border/80 bg-background/95 px-4 py-3 backdrop-blur md:hidden">
          <div className="container flex items-center gap-3 px-0">
            {stickyCta}
          </div>
        </div>
      ) : null}
    </div>
  );
};

type CampaignHeaderCtaProps = {
  label: string;
  href: string;
};

export const CampaignHeaderCta = ({ label, href }: CampaignHeaderCtaProps) => (
  <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90">
    <a href={href}>
      {label}
      <ArrowRight className="h-4 w-4" />
    </a>
  </Button>
);

type CampaignSupportLinkProps = {
  className?: string;
};

export const CampaignSupportLink = ({ className }: CampaignSupportLinkProps) => (
  <a
    href={CAMPAIGN_SUPPORT_HREF}
    className={cn(
      "inline-flex items-center gap-2 text-sm text-muted-foreground transition hover:text-foreground",
      className,
    )}
  >
    <Mail className="h-4 w-4" />
    support@shadowscan.com.au
    <ExternalLink className="h-3.5 w-3.5" />
  </a>
);
